<template>
  <div class="date-range-slider" @click="closeAllPickers">
    <div class="date-range-header">
      <label v-if="name" class="slider-label">{{ name }}</label>
      <div class="date-range-display">
        <div class="date-picker-wrapper">
          <span 
            class="date-value clickable" 
            @click.stop="showStartPicker = !showStartPicker"
            :class="{ active: showStartPicker }"
          >
            {{ formatDate(selectedStart) }}
          </span>
          <Calendar
            v-if="showStartPicker"
            v-model="selectedStart"
            class="date-picker-overlay"
            @update:modelValue="onStartDateChange"
            @hide="showStartPicker = false"
            :min-date="startDate"
            :max-date="selectedEnd"
            inline
            @click.stop
          />
        </div>
        <span class="date-separator">—</span>
        <div class="date-picker-wrapper">
          <span 
            class="date-value clickable" 
            @click.stop="showEndPicker = !showEndPicker"
            :class="{ active: showEndPicker }"
          >
            {{ formatDate(selectedEnd) }}
          </span>
          <Calendar
            v-if="showEndPicker"
            v-model="selectedEnd"
            class="date-picker-overlay"
            @update:modelValue="onEndDateChange"
            @hide="showEndPicker = false"
            :min-date="selectedStart"
            :max-date="endDate"
            inline
            @click.stop
          />
        </div>
      </div>
    </div>
    
    <div class="slider-container">
      <Slider
        v-model="sliderValues"
        :min="0"
        :max="maxDays"
        :step="stepDays"
        range
        class="date-slider"
        @update:modelValue="onSliderChange"
      />
    </div>
    
    <div class="date-range-bounds">
      <span class="bound-date">{{ formatDate(startDate) }}</span>
      <span class="bound-date">{{ formatDate(endDate) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import Slider from 'primevue/slider'
import Calendar from 'primevue/calendar'

export interface DateRangeSliderProps {
  name?: string
  start: Date
  end: Date
  value?: [Date, Date]
  step?: number // step in days
  modelValue?: [Date, Date]
}

const props = withDefaults(defineProps<DateRangeSliderProps>(), {
  name: '',
  step: 1,
  value: undefined,
  modelValue: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: [Date, Date]]
  'change': [value: [Date, Date]]
}>()

// Convert dates to days since start for slider calculations
const startDate = ref<Date>(new Date(props.start))
const endDate = ref<Date>(new Date(props.end))

// State for date picker visibility
const showStartPicker = ref(false)
const showEndPicker = ref(false)

// Calculate the total range in days
const maxDays = computed(() => {
  return Math.floor((endDate.value.getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
})

const stepDays = computed(() => props.step)

// Initialize slider values
const getInitialValues = (): [number, number] => {
  const initialValue = props.modelValue || props.value
  if (initialValue) {
    const startDays = Math.floor((initialValue[0].getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    const endDays = Math.floor((initialValue[1].getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    return [Math.max(0, startDays), Math.min(maxDays.value, endDays)]
  }
  return [0, maxDays.value]
}

const sliderValues = ref<[number, number]>(getInitialValues())

// Convert slider values back to dates
const selectedStart = computed(() => {
  const days = sliderValues.value[0]
  const date = new Date(startDate.value)
  date.setDate(date.getDate() + days)
  return date
})

const selectedEnd = computed(() => {
  const days = sliderValues.value[1]
  const date = new Date(startDate.value)
  date.setDate(date.getDate() + days)
  return date
})

// Handle slider changes
const onSliderChange = (value: number | number[]) => {
  let values: [number, number]
  if (Array.isArray(value)) {
    values = [value[0] || 0, value[1] || maxDays.value] as [number, number]
  } else {
    values = [0, value]
  }
  sliderValues.value = values
  const dateRange: [Date, Date] = [selectedStart.value, selectedEnd.value]
  emit('update:modelValue', dateRange)
  emit('change', dateRange)
}

// Format date for display
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }).replace(/(\d{2}) (\w{3}) (\d{4})/, '$1 $2 $3')
}

// Handle direct date changes from pickers
const onStartDateChange = (newDate: Date) => {
  if (newDate && newDate <= selectedEnd.value) {
    const startDays = Math.floor((newDate.getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    sliderValues.value[0] = Math.max(0, startDays)
    showStartPicker.value = false
    const dateRange: [Date, Date] = [selectedStart.value, selectedEnd.value]
    emit('update:modelValue', dateRange)
    emit('change', dateRange)
  }
}

const onEndDateChange = (newDate: Date) => {
  if (newDate && newDate >= selectedStart.value) {
    const endDays = Math.floor((newDate.getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    sliderValues.value[1] = Math.min(maxDays.value, endDays)
    showEndPicker.value = false
    const dateRange: [Date, Date] = [selectedStart.value, selectedEnd.value]
    emit('update:modelValue', dateRange)
    emit('change', dateRange)
  }
}

// Handle clicking outside to close pickers
const closeAllPickers = () => {
  showStartPicker.value = false
  showEndPicker.value = false
}

// Watch for external changes to props
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    const startDays = Math.floor((newValue[0].getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    const endDays = Math.floor((newValue[1].getTime() - startDate.value.getTime()) / (1000 * 60 * 60 * 24))
    sliderValues.value = [Math.max(0, startDays), Math.min(maxDays.value, endDays)]
  }
})

watch(() => props.start, (newStart) => {
  startDate.value = new Date(newStart)
  sliderValues.value = getInitialValues()
})

watch(() => props.end, (newEnd) => {
  endDate.value = new Date(newEnd)
  sliderValues.value = getInitialValues()
})

onMounted(() => {
  // Emit initial value
  if (props.modelValue || props.value) {
    const initialValue = props.modelValue || props.value!
    emit('update:modelValue', initialValue)
  } else {
    const dateRange: [Date, Date] = [selectedStart.value, selectedEnd.value]
    emit('update:modelValue', dateRange)
  }
})
</script>

<style scoped>
.date-range-slider {
  width: 100%;
  padding: 1rem 0;
}

.date-range-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.slider-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color);
}

.date-range-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

.date-picker-wrapper {
  position: relative;
  border: 1px solid var(--p-multiselect-border-color);
  border-radius: 6px;
}

.date-value {
  background: var(--surface-100);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--surface-300);
  font-weight: 500;
  transition: all 0.2s ease;
}

.date-value.clickable {
  cursor: pointer;
  user-select: none;
}

.date-value.clickable:hover {
  background: var(--surface-200);
  border-color: var(--primary-color);
}

.date-value.clickable.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.date-picker-overlay {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: white;
  border: 1px solid var(--surface-300);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
  min-width: 400px;
}

.date-separator {
  color: var(--text-color-secondary);
  font-weight: bold;
}

.slider-container {
  margin: 1.5rem 0;
  padding: 0 0.5rem;
}

.date-slider {
  width: 100%;
}

.date-range-bounds {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
}

.bound-date {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  font-family: monospace;
}

/* Dark theme support - DISABLED */
/*
@media (prefers-color-scheme: dark) {
  .date-value {
    background: var(--surface-800);
    border-color: var(--surface-600);
  }
  
  .date-value.clickable:hover {
    background: var(--surface-700);
  }
  
  .date-picker-overlay {
    background: var(--surface-800);
    border-color: var(--surface-600);
  }
}
*/

/* Custom slider styling for better date range visualization */
:deep(.p-slider-range) {
  background: linear-gradient(90deg, var(--primary-color), var(--primary-color-text));
}

:deep(.p-slider-handle) {
  border: 2px solid var(--primary-color);
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.p-slider-handle:hover) {
  border-color: var(--primary-color);
  background: var(--surface-50);
}

:deep(.p-slider-handle:focus) {
  box-shadow: 0 0 0 0.2rem var(--primary-color-text);
}
</style>